import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useAuthStore } from '~/lib/auth/auth-store';

function initials(firstName: string | null, lastName: string | null, email: string | null): string {
	const a = firstName?.trim()?.[0] ?? '';
	const b = lastName?.trim()?.[0] ?? '';
	const combined = (a + b).toUpperCase();
	if (combined) return combined;
	return (email?.trim()?.[0] ?? '?').toUpperCase();
}

function displayName(firstName: string | null, lastName: string | null, email: string | null): string {
	const full = [firstName, lastName].filter(Boolean).join(' ').trim();
	return full || email || '';
}

/** Topbar user menu: avatar + name trigger with profile/settings/activity links and logout. */
export function UserMenu() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const session = useAuthStore(s => s.session);
	const signOut = useAuthStore(s => s.signOut);

	const name = displayName(session?.firstName ?? null, session?.lastName ?? null, session?.email ?? null);

	const handleSignOut = () => {
		signOut();
		navigate('/login', { replace: true });
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="flex h-[30px] items-center gap-2 rounded-md pr-2 pl-1 hover:bg-muted focus-visible:outline-none">
				<span className="flex size-6 items-center justify-center rounded-full bg-teal-300 text-[10.5px] font-medium text-teal-700">
					{initials(session?.firstName ?? null, session?.lastName ?? null, session?.email ?? null)}
				</span>
				<span className="max-w-[12ch] truncate text-[11.5px] font-medium text-foreground">{name}</span>
				<ChevronDown size={11} className="text-foreground-subtle" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="min-w-[200px]">
				<DropdownMenuItem onSelect={() => navigate('/profile')}>{t('shell.user.profile')}</DropdownMenuItem>
				<DropdownMenuItem onSelect={() => navigate('/activity')}>{t('shell.user.activity')}</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem variant="destructive" onSelect={handleSignOut}>
					{t('shell.user.signOut')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
